import React from 'react';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import './App.css';
import { useZone } from './contexts/ZoneContext';

// 移动端组件
import MatchList from './components/MatchList';
import MobilePlayerDetail from './components/player/MobilePlayerDetail';
import MobilePlayerList from './components/MobilePlayerList';
import MobileRankings from './components/MobileRankings';
import MobileAddMatch from './components/MobileAddMatch';
import MobileDoublesPartnerStats from './components/MobileDoublesPartnerStats';
import ZoneSelector from './components/ZoneSelector';
// import EventList from './components/EventList'; // 未使用，已注释
import ZoneList from './components/ZoneList';
import HomePage from './components/HomePage';
import MobileBottomNav from './components/MobileBottomNav';
import MobileEventManagement from './components/MobileEventManagement';
import MobileAddPlayer from './components/player/MobileAddPlayer';
import MobileEditPlayer from './components/player/MobileEditPlayer';
import MobileMorePage from './components/MobileMorePage';
import CourtUsageHome from './components/courtUsage/CourtUsageHome';
import PrivateFundHome from './components/privateFund/PrivateFundHome';
import ProductDetail from './components/privateFund/ProductDetail';
import ProductForm from './components/privateFund/ProductForm';
import NetValueUpdate from './components/privateFund/NetValueUpdate';
import NetValueHistory from './components/privateFund/NetValueHistory';
import RedemptionForm from './components/privateFund/RedemptionForm';
import DividendForm from './components/privateFund/DividendForm';
import InvestorDetail from './components/privateFund/InvestorDetail';
import KnowledgeLotteryHome from './views/KnowledgeLotteryHome';
import KnowledgeLotteryManage from './views/KnowledgeLotteryManage';
import KnowledgeLotteryDetail from './views/KnowledgeLotteryDetail';
import KnowledgeLotterySessionDetail from './views/KnowledgeLotterySessionDetail';
import KnowledgeLotterySessionList from './views/KnowledgeLotterySessionList';
import KnowledgeLotteryBoard from './views/KnowledgeLotteryBoard';
import KnowledgeLotteryStatistics from './views/KnowledgeLotteryStatistics';
import UserActivityStatistics from './views/UserActivityStatistics';
import Login from './views/Login';
import ProtectedRoute from './components/ProtectedRoute';
import KnowledgeManagementRoute from './components/KnowledgeManagementRoute';
import PrivateFundRoute from './components/PrivateFundRoute';
import HealthRoute from './components/HealthRoute';
import SystemAdminRoute from './components/SystemAdminRoute';
import TravelMemoriesRoute from './components/TravelMemoriesRoute';
import TravelMemoriesHome from './views/TravelMemoriesHome';
import TravelMemoriesSpaceSelect from './views/TravelMemoriesSpaceSelect';
import TravelMemoriesSpaceManage from './views/TravelMemoriesSpaceManage';
import TravelMemoriesTravelDetail from './views/TravelMemoriesTravelDetail';
import TravelMemoriesTravelEdit from './views/TravelMemoriesTravelEdit';
import TravelMemoriesTravelShare from './views/TravelMemoriesTravelShare';
import { TravelMemoriesProvider } from './contexts/TravelMemoriesContext';
import HealthHome from './views/HealthHome';
import DietPlan from './views/DietPlan';

/**
 * 应用程序主组件
 * 移动端专用版本
 * 
 * @returns {JSX.Element} 应用程序主组件
 */
const App = () => {
  const location = useLocation();
  // Hooks必须在条件判断之前调用
  const { currentZone } = useZone(); // 从上下文获取当前区域
  const defaultZone = currentZone || process.env.REACT_APP_DEFAULT_ZONE || 'beijing';
  
  // 如果是登录页，直接返回登录组件，不加载其他逻辑
  if (location.pathname === '/login') {
    return <Login />;
  }
  
  // 判断是否应该显示底部导航栏
  // 在首页、场地管理、私募产品管理、知识抽查系统、旅行回忆系统页面隐藏底部导航栏
  const shouldShowBottomNav = () => {
    const pathname = location.pathname;
    return pathname !== '/' && 
           pathname !== '/badminton' &&
           !pathname.startsWith('/court-usage') &&
           !pathname.startsWith('/private-fund') &&
           !pathname.startsWith('/knowledge-lottery') &&
           !pathname.startsWith('/travel-memories') &&
           !pathname.startsWith('/health');
  };
  
  // 区域内布局组件
  const ZoneLayout = () => {
    return (
      <>
        <ZoneSelector />
        <Outlet />
      </>
    );
  };

  return (
    <div className="app mobile-app">
      <main className="main-content">
        <Routes>
          {/* 登录页 */}
          <Route path="/login" element={<Login />} />
          
          {/* 主页 */}
          <Route path="/" element={<HomePage />} />
          
          {/* 场地管理路由 */}
          <Route path="/court-usage" element={<CourtUsageHome />} />
          <Route path="/court-usage/home" element={<CourtUsageHome />} />
          
          {/* 私募产品管理路由 - 需要登录和权限 */}
          <Route path="/private-fund" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <PrivateFundHome />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/home" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <PrivateFundHome />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/products/new" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <ProductForm />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/products/:id" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <ProductDetail />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/products/:id/edit" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <ProductForm />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/products/:id/net-value" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <NetValueUpdate />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/products/:id/net-value-history" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <NetValueHistory />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/products/:id/redemption" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <RedemptionForm />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/products/:id/dividend" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <DividendForm />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          <Route path="/private-fund/investors/:id" element={
            <ProtectedRoute>
              <PrivateFundRoute>
                <InvestorDetail />
              </PrivateFundRoute>
            </ProtectedRoute>
          } />
          
          {/* 知识抽查系统路由 - 需要登录 */}
          <Route path="/knowledge-lottery" element={
            <ProtectedRoute>
              <KnowledgeLotteryHome />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-lottery/manage" element={
            <ProtectedRoute>
              <KnowledgeManagementRoute>
                <KnowledgeLotteryManage />
              </KnowledgeManagementRoute>
            </ProtectedRoute>
          } />
          <Route path="/knowledge-lottery/knowledge-board" element={
            <ProtectedRoute>
              <KnowledgeLotteryBoard />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-lottery/statistics" element={
            <ProtectedRoute>
              <KnowledgeLotteryStatistics />
            </ProtectedRoute>
          } />
          
          {/* 用户行为统计路由 - 只有系统管理员可以访问 */}
          <Route path="/statistics/user-activity" element={
            <ProtectedRoute>
              <SystemAdminRoute>
                <UserActivityStatistics />
              </SystemAdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/knowledge-lottery/knowledge/:id" element={
            <ProtectedRoute>
              <KnowledgeLotteryDetail />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-lottery/session/:id" element={
            <ProtectedRoute>
              <KnowledgeLotterySessionDetail />
            </ProtectedRoute>
          } />
          <Route path="/knowledge-lottery/sessions" element={
            <ProtectedRoute>
              <KnowledgeLotterySessionList />
            </ProtectedRoute>
          } />
          
          {/* 旅行回忆系统路由 - 需要登录和权限 */}
          <Route path="/travel-memories" element={
            <ProtectedRoute>
              <TravelMemoriesRoute>
                <TravelMemoriesProvider>
                  <TravelMemoriesHome />
                </TravelMemoriesProvider>
              </TravelMemoriesRoute>
            </ProtectedRoute>
          } />
          <Route path="/travel-memories/spaces" element={
            <ProtectedRoute>
              <TravelMemoriesRoute>
                <TravelMemoriesProvider>
                  <TravelMemoriesSpaceSelect />
                </TravelMemoriesProvider>
              </TravelMemoriesRoute>
            </ProtectedRoute>
          } />
          <Route path="/travel-memories/spaces/new" element={
            <ProtectedRoute>
              <TravelMemoriesRoute>
                <TravelMemoriesProvider>
                  <TravelMemoriesSpaceManage />
                </TravelMemoriesProvider>
              </TravelMemoriesRoute>
            </ProtectedRoute>
          } />
          <Route path="/travel-memories/spaces/:id" element={
            <ProtectedRoute>
              <TravelMemoriesRoute>
                <TravelMemoriesProvider>
                  <TravelMemoriesSpaceManage />
                </TravelMemoriesProvider>
              </TravelMemoriesRoute>
            </ProtectedRoute>
          } />
          <Route path="/travel-memories/spaces/:spaceId/travels/:travelId" element={
            <ProtectedRoute>
              <TravelMemoriesRoute>
                <TravelMemoriesProvider>
                  <TravelMemoriesTravelDetail />
                </TravelMemoriesProvider>
              </TravelMemoriesRoute>
            </ProtectedRoute>
          } />
          <Route path="/travel-memories/spaces/:spaceId/travels/:travelId/edit" element={
            <ProtectedRoute>
              <TravelMemoriesRoute>
                <TravelMemoriesProvider>
                  <TravelMemoriesTravelEdit />
                </TravelMemoriesProvider>
              </TravelMemoriesRoute>
            </ProtectedRoute>
          } />
          
          {/* 旅行分享页面（公开，不需要权限） */}
          <Route path="/travel-memories/share/:travelId" element={
            <TravelMemoriesTravelShare />
          } />

          {/* 健康管理路由 - 需登录+健康权限，仅本人数据 */}
          <Route path="/health" element={
            <ProtectedRoute>
              <HealthRoute>
                <HealthHome />
              </HealthRoute>
            </ProtectedRoute>
          } />
          <Route path="/health/diet-plan" element={
            <ProtectedRoute>
              <HealthRoute>
                <DietPlan />
              </HealthRoute>
            </ProtectedRoute>
          } />
          
          {/* 羽毛球数据区域路由 */}
          <Route path="/badminton" element={<ZoneList />} />
          
          {/* 具体区域的路由 */}
          <Route path="/badminton/:zone" element={<ZoneLayout />}>
            {/* 默认显示活动列表，使用MobileEventManagement组件处理 */}
            <Route index element={<MobileEventManagement />} />
            
            {/* 活动相关路由 */}
            <Route path="events" element={<MobileEventManagement />} />
            <Route path="events/:eventId" element={<MobileEventManagement />} />
            <Route path="events/:eventId/matches/new" element={<MobileAddMatch />} />
            
            {/* 添加比赛路由 - 使用sessionStorage存储活动ID */}
            <Route path="add-match" element={<MobileAddMatch />} />
            
            {/* 比赛相关路由 */}
            <Route path="matches" element={<MatchList />} />
            <Route path="matches/:id" element={<MobilePlayerDetail />} />
            <Route path="matches/create" element={<MobileAddMatch />} />
            
            {/* 球员相关路由 */}
            <Route path="players" element={<MobilePlayerList />} />
            <Route path="players/create" element={<MobileAddPlayer />} />
            <Route path="players/:id" element={<MobilePlayerDetail />} />
            <Route path="players/:id/edit" element={<MobileEditPlayer />} />
            <Route path="players/:id/doubles-partners" element={<MobileDoublesPartnerStats />} />
            
            {/* 排名路由 */}
            <Route path="rankings" element={<MobileRankings />} />
            
            {/* 更多功能路由 */}
            <Route path="more" element={<MobileMorePage />} />
          </Route>
          
          {/* 默认区域路由（保持向后兼容） */}
          <Route path="/matches" element={<Navigate to={`/badminton/${defaultZone}/matches`} replace />} />
          <Route path="/matches/:id" element={<Navigate to={`/badminton/${defaultZone}/matches/:id`} replace />} />
          <Route path="/matches/create" element={<Navigate to={`/badminton/${defaultZone}/matches/create`} replace />} />
          <Route path="/players" element={<Navigate to={`/badminton/${defaultZone}/players`} replace />} />
          <Route path="/players/create" element={<Navigate to={`/badminton/${defaultZone}/players/create`} replace />} />
          <Route path="/players/:id" element={<Navigate to={`/badminton/${defaultZone}/players/:id`} replace />} />
          <Route path="/players/:id/doubles-partners" element={<Navigate to={`/badminton/${defaultZone}/players/:id/doubles-partners`} replace />} />
          <Route path="/rankings" element={<Navigate to={`/badminton/${defaultZone}/rankings`} replace />} />
          <Route path="/events" element={<Navigate to={`/badminton/${defaultZone}/events`} replace />} />
          <Route path="/more" element={<Navigate to={`/badminton/${defaultZone}/more`} replace />} />
          
          {/* 404页面 */}
          <Route path="*" element={
            <div className="not-found">
              <h2>404 - 页面未找到</h2>
              <p>您访问的页面不存在。</p>
            </div>
          } />
        </Routes>
      </main>
      
      {/* 全局底部导航栏 - 在私募产品管理和场地管理页面隐藏 */}
      {shouldShowBottomNav() && <MobileBottomNav />}
    </div>
  );
};

export default App;